import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
	archiveOrganization,
	createNewOrganization,
	editOrganization,
	getChildrenOrganization,
	getCodeList,
	getMarkerList,
	getOkiiStatuses,
	getOrganizationLogo,
	getOrganizations,
	getOrganizationsSearched,
	getParentSelectedOrganization,
	getSelectedOrganization,
	getSubsidiaries,
	restoreOrganization,
	setDocumentKii,
	setMarkers,
	setOrganizationLogo,
	switchCategorizationRule,
} from "./organizations.thunks";
import { IHistoryChange, IMarker } from "./types";
import { isEmpty } from "helpers";
import {
	OrganizationCardResponse,
	OrganizationObjectStatusResponse, OrganizationOkvedResponse,
	OrganizationResponse,
} from "libs/apiClient/data-contracts";

interface OrganizationState {
	organizationsList: OrganizationResponse[];
	selectedOrganization: OrganizationCardResponse | null;
	iconSelectedOrganization: string | null;
	parentSelectedOrganization: OrganizationCardResponse | null;
	subsidiariesList: OrganizationResponse[];
	historyChanges: IHistoryChange[];
	markerList: {
		system: {
			abbreviation?: string | null;
			colorCode?: string | null;
			comment?: string | null;
			document?: {
				key: string;
				name: string;
			} | null;
			id: number;
			name: string;
		};
		user: IMarker[];
	};
	codeList: OrganizationOkvedResponse[];
	mode: string;
	addingOrg: boolean;
	searchValue: string;
	searchMode: boolean;
	searchPending: boolean;
	searchedList: OrganizationResponse[];
	statusesOkii: OrganizationObjectStatusResponse[];
	archiveMode: boolean;
}

const initialState: OrganizationState = {
	organizationsList: [],
	selectedOrganization: null,
	iconSelectedOrganization: null,
	parentSelectedOrganization: null,
	subsidiariesList: [],
	historyChanges: [],
	markerList: { system: {} as any, user: [] },
	codeList: [],
	mode: "",
	addingOrg: false,
	searchValue: "",
	searchMode: false,
	searchPending: false,
	searchedList: [],
	statusesOkii: [],
	archiveMode: false,
};

const organizationsSlice = createSlice({
	name: "organizationsSlice",
	initialState,
	reducers: {
		setHistory(state, action: PayloadAction<IHistoryChange[]>) {
			state.historyChanges = action.payload;
		},

		updateHistory(state, action: PayloadAction<IHistoryChange[]>) {
			state.historyChanges = [...state.historyChanges, ...action.payload];
		},

		changeMode(state, action: PayloadAction<string>) {
			state.mode = action.payload;
		},
		ChangeSearchValue(state, action: PayloadAction<string>) {
			state.searchValue = action.payload;
			if (!state.searchValue.trim()) {
				state.searchedList = [];
				state.searchMode = false;
				state.searchPending = false;
			} else {
				state.searchMode = false;
				state.searchMode = true;
				state.searchPending = true;
			}
		},
		ClearSearch(state) {
			state.searchValue = "";
			state.searchMode = false;
			state.searchPending = false;
			state.searchedList = [];
		},
		ClearSelectedOrganization(state) {
			state.selectedOrganization = null;
			state.subsidiariesList = [];
			state.iconSelectedOrganization = null;
		},
		ClearOrganizations(state, action: PayloadAction) {
			state.organizationsList = [];
		},
		ChangeArchiveMode(state, action: PayloadAction<boolean>) {
			state.archiveMode = action.payload;
		},
		ChangeAddingOrg(state, action: PayloadAction<boolean>) {
			state.addingOrg = action.payload;
		},
	},

	extraReducers: (builder) => {
		builder.addCase(getOrganizations.fulfilled, (state, action) => {
			state.organizationsList.push(...action.payload);
		});
		builder.addCase(getSelectedOrganization.fulfilled, (state, action: PayloadAction<any>) => {
			state.selectedOrganization = {
				...action.payload,
				...(action.payload.kiiDocument && {
					kiiDocument: {
						...action.payload.kiiDocument,
						key: action.payload.kiiDocument.key + "/" + action.payload.kiiDocument.name,
					},
				}),
			};
		});
		builder.addCase(getParentSelectedOrganization.fulfilled, (state, action) => {
			state.parentSelectedOrganization = action.payload;
		});
		builder.addCase(getChildrenOrganization.fulfilled, (state, action) => {
			state.organizationsList.push(...action.payload);
		});
		builder.addCase(createNewOrganization.fulfilled, (state, action) => {
			let parentIndex = state.organizationsList.findIndex(
				(organization) => organization.id === action.payload.parent?.id,
			);
			if (parentIndex >= 0) {
				state.organizationsList[parentIndex].childrenCount++;
			} else if (
				state.selectedOrganization?.id === action.payload.parent?.id &&
				action.payload.parent?.id &&
				state.selectedOrganization?.id
			) {
				state.selectedOrganization.childrenCount++;
			}

			if (action.payload.parent?.id && state.selectedOrganization?.id === action.payload.parent?.id) {
				state.subsidiariesList.push(action.payload);
			} else if (parentIndex >= 0 && state.organizationsList[parentIndex]?.childrenCount) {
				state.organizationsList.push(action.payload);
			} else if (action.payload.type.name === "Головная организация") {
				state.organizationsList.push(action.payload);
			}
			state.addingOrg = true;
		});
		builder.addCase(archiveOrganization.fulfilled, (state, action) => {
			if (state.searchMode) {
				state.searchedList = state.searchedList.filter((org) => org.id !== action.payload.id);
			} else {
				state.organizationsList = state.organizationsList.filter((org) => org.id !== action.payload.id);
				const parentIndex = state.organizationsList.findIndex((org) => org.id === action.payload.parent?.id);
				state.organizationsList[parentIndex].childrenCount--;
			}
		});
		builder.addCase(restoreOrganization.fulfilled, (state, action) => {
			if (state.searchMode) {
				state.searchedList = state.searchedList.filter((org) => org.id !== action.payload.id);
			} else {
				state.organizationsList = state.organizationsList.filter((org) => org.id !== action.payload.id);
			}
		});
		builder.addCase(getOrganizationsSearched.fulfilled, (state, action) => {
			state.searchedList = action.payload;
			state.searchPending = false;
		});
		builder.addCase(getSubsidiaries.fulfilled, (state, action) => {
			state.subsidiariesList = action.payload;
		});
		builder.addCase(editOrganization.fulfilled, (state, action) => {
			state.selectedOrganization = {
				...action.payload,
				...(action.payload.kiiDocument && {
					kiiDocument: {
						...action.payload.kiiDocument,
						key: action.payload.kiiDocument.key + "/" + action.payload.kiiDocument.name,
					},
				}),
			};
		});
		builder.addCase(setOrganizationLogo.fulfilled, (state, action) => {
			state.iconSelectedOrganization = action.payload;
		});
		builder.addCase(getOrganizationLogo.fulfilled, (state, action) => {
			state.iconSelectedOrganization = action.payload;
		});
		builder.addCase(getMarkerList.fulfilled, (state, action) => {
			state.markerList.user = action.payload.user;
			state.markerList.system = !isEmpty(action.payload.system) ? action.payload.system.KII : ({} as any);
		});
		builder.addCase(setMarkers.fulfilled, (state, action) => {
			state.markerList.user = action.payload.user;
			state.markerList.system = !isEmpty(action.payload.system) ? action.payload.system : ({} as any);
		});
		builder.addCase(getCodeList.fulfilled, (state, action) => {
			state.codeList = action.payload;
		});
		builder.addCase(switchCategorizationRule.fulfilled, (state, action) => {
			const ids = state.subsidiariesList.map((item) => item.id);
			const candidateIndex = ids.indexOf(action.payload.orgId);

			if (candidateIndex !== -1) {
				state.subsidiariesList[candidateIndex].categorizable = !!action.payload.categorizable;
			}
		});
		builder.addCase(getOkiiStatuses.fulfilled, (state, action) => {
			state.statusesOkii = action.payload;
		});
		builder.addCase(setDocumentKii.fulfilled, (state, action: PayloadAction<any>) => {
			// <{ name: string; key: string }>
			if (action.payload && state.selectedOrganization) state.selectedOrganization.kiiDocument = { ...action.payload };
		});
	},
});

export const {
	setHistory,
	updateHistory,
	changeMode,
	ChangeSearchValue,
	ClearSearch,
	ClearSelectedOrganization,
	ClearOrganizations,
	ChangeArchiveMode,
	ChangeAddingOrg
} = organizationsSlice.actions;
export default organizationsSlice.reducer;
